import { Capacitor } from "@capacitor/core";

import { API_URL } from "./env";

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

let unauthorizedHandler: (() => void) | undefined;
function setUnauthorizedHandler(handler?: () => void) {
  unauthorizedHandler = handler;
}

type FetchSafeOptions = {
  body?: string;
  timeout?: number;
  method?: string;
};

type FetchStatus = {
  code: number;
  reason?: string;
  extra?: any;
};

// Send a request to a URL
// Returns an array of the desired response and an error containing a string reason/response HTTP code
async function fetchSafe<RetT>(
  url: string,
  options: FetchSafeOptions | null = null
): Promise<[RetT | null, FetchStatus]> {
  const timeout = options?.timeout ?? 60_000;

  try {
    const controller = new AbortController();
    const handle = setTimeout(() => {
      controller.abort();
    }, timeout);

    let response!: Response;

    if (options?.body == null) {
      response = await fetch(url, {
        signal: controller.signal,
        method: options?.method ?? "POST",
        credentials: "include",
      });
    } else {
      response = await fetch(url, {
        signal: controller.signal,
        // It's a lot harder to get an end user to send a POST request on accident/maliciously
        // So the entire API consists entirely of POST requests
        method: options.method ?? "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: options.body,
        credentials: "include",
      });
    }

    clearTimeout(handle);

    if (response.status === 401 && unauthorizedHandler) {
      unauthorizedHandler();
    }

    if (response.ok) {
      const retText = await response.text();

      if (retText.length === 0) {
        return [null as RetT, { code: response.status }];
      } else {
        return [JSON.parse(retText) as RetT, { code: response.status, extra: JSON.parse(retText) as unknown }];
      }
    } else {
      const retText = await response.text();

      if (retText.length === 0) {
        return [null as RetT, { code: response.status }];
      } else {
        return [
          null,
          {
            code: response.status,
            reason: JSON.parse(retText).reason as string,
            extra: JSON.parse(retText) as unknown,
          },
        ];
      }
    }
  } catch {
    return [null, { code: 400 }];
  }
}

// Send data to the API server, and get a response through a callback
async function postApi<RetT = never>(
  path: string,
  body: object | null,
  complete: (res: RetT) => void,
  error: (err: FetchStatus) => void
) {
  const bodyString = body ? JSON.stringify(body) : "";

  const [res, resError] = await fetchSafe<RetT>(API_URL + path, {
    body: bodyString,
  });

  if (resError.code != 200 || !res) {
    console.log("HTTP Error", resError);
    error(resError);
  } else {
    console.log("HTTP response", res);
    complete(res);
  }
}

// Send data to the API server, expecting no response
async function sendApi(path: string, body: object | null, complete: () => void, error: (err: FetchStatus) => void) {
  const bodyString = body ? JSON.stringify(body) : "";

  const [res, resError] = await fetchSafe<unknown>(API_URL + path, {
    body: bodyString,
  });

  if (resError.code == 200) {
    complete();
  } else {
    console.log("HTTP Error", resError);
    error(resError);
  }
}

function assert(cond: boolean): asserts cond {
  if (!cond) {
    throw new Error("Assertion failed");
  }
}

function assertExists<T>(obj: T | null | undefined): asserts obj is NonNullable<T> {
  assert(obj != null);
}

function isNative() {
  return Capacitor.getPlatform() !== "web";
}

export {
  assert,
  assertExists,
  fetchSafe,
  FetchSafeOptions,
  FetchStatus,
  isNative,
  postApi,
  sendApi,
  setUnauthorizedHandler,
  sleep,
};
